import { isEmpty, isEqual } from 'lodash';
import { rgba } from 'polished';
import * as React from 'react'
import { Image, StyleSheet, TextStyle, TouchableOpacity, View, ViewProps, ViewStyle } from 'react-native'
import GoogleMapReact from 'google-map-react'
import Card from '@bit/jimmijoensson.keyhole.components.card';
import { theme, themeStyles } from '@bit/jimmijoensson.keyhole.components.keyhole-theme';
import Divider from '@bit/jimmijoensson.keyhole.components.divider';
import Paragraph from '@bit/jimmijoensson.keyhole.paragraph';
import { useKeyholeListingContext } from '../../contexts/KeyholeListingContext/KeyholeListingContext';
import InfoCard from '@bit/jimmijoensson.keyhole.components.info-card';
import InfoSection from '@bit/jimmijoensson.keyhole.components.info-section';
import KeyholeTouchableOpacity from '@bit/jimmijoensson.keyhole.components.keyhole-touchable-opacity';
import Br from '@bit/jimmijoensson.keyhole.components.br';
import KeyholeDepositAutoCompleteCard, {AutoCompleteResultInterface} from '@bit/jimmijoensson.keyhole.components.keyhole-deposit-autocomplete-card';
import { ComplexListingMinimal } from '@bit/jimmijoensson.keyhole.interfaces.complex-listing-interface';
import { initialPagination } from '@bit/jimmijoensson.keyhole.interfaces.paginated-response-interface';
import PopulationBar from '@bit/jimmijoensson.keyhole.components.population-bar';
import Select from '@bit/jimmijoensson.keyhole.components.select';
import BackgroundLayout from '@bit/jimmijoensson.keyhole.layouts.background-layout';
import { Props } from '@bit/jimmijoensson.keyhole.components.select/node_modules/@types/react-select';
import FetchMoreButton from '@bit/jimmijoensson.keyhole.components.fetch-more-button';
import Marker from '@bit/jimmijoensson.keyhole.components.marker'
import BuildingIcon from '@bit/jimmijoensson.keyhole.icons.building-icon'
import Heading from '@bit/jimmijoensson.keyhole.components.heading';
import { getComplexDetails, getShapeUrl } from '../../utils/helpers';
import { useResponsiveContext } from '../../contexts/ResponsiveContext/ResponsiveContext';
import { nextPaginatedParams, openInNewTab, setAsyncTimeout } from '../../utils/utils';
import { DEV_DOMAIN } from '../../utils/constants';
import { getPaginationParams, GetParams, Pagination } from '../../interfaces/ComplexListingResponse';


export interface KeyholeListingsProps extends ViewProps {
  //Props goes here
}

let parsedUrl = new URL(window.location.href)
let parsedParams = new URLSearchParams(parsedUrl.search)

const hasParams = () => (
  !isEmpty(parsedParams.get('s')) ||
  !isEmpty(parsedParams.get('udlejer'))
)

const initialComplexPagination:Pagination = {
  ...initialPagination,
  s: parsedParams.get('s') || '',
}

const KeyholeListings = ({ ...props }: KeyholeListingsProps) => {
  const { dims } = useResponsiveContext()
  const { darLookup, fetchComplexes, filters } = useKeyholeListingContext()
  const [complexes, setComplexes] = React.useState<ComplexListingMinimal[]>([])
  const [complexesPagination, setComplexesPagination] = React.useState<Pagination>(initialComplexPagination)
  const [searchStr, setSearchStr] = React.useState(parsedParams.get('s') || '')
  const [selectedLandlord, setSelectedLandlord] = React.useState<Props['value'] | null>(null)
  const [hasSearched, setHasSearched] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState<boolean>(hasParams())

  const getComplexes = React.useCallback(async (params: GetParams, append?: boolean) => {
    try {
      setIsLoading(true)
      const { items, ...response} = await fetchComplexes({
        endpoint: '/webflow/complexes',
        params: params,
        cached: false,
      })
      setAsyncTimeout(400)

      setComplexesPagination(response)
      setComplexes(x => append ? [...x, ...items] : items)
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      throw error
    }
  }, [fetchComplexes])

  const updateSelectedLandlord = React.useCallback((value: Props['value']) => {
    setSelectedLandlord(value)
    setComplexesPagination(x => ({...x, orgId: value?.value}))
    parsedParams.set('udlejer', value?.label || '')
    parsedUrl.search = parsedParams.toString()
    window.history.replaceState({}, '', parsedUrl.toString())
  }, [])

  const handleSelectedLandlordClear = React.useCallback(() => {
    setSelectedLandlord(null)
    parsedParams.delete('udlejer')
    parsedUrl.search = parsedParams.toString()
    window.history.replaceState({}, '', parsedUrl.toString())
  }, [])

  const handleClear = React.useCallback(() => {
    setSearchStr('')
    setHasSearched(false)
    parsedParams.delete('s')
    parsedUrl.search = parsedParams.toString()
    window.history.replaceState({}, '', parsedUrl.toString())
  }, [])

  const handleFiltersClear = React.useCallback(async (filters: ('landlord')[]) => {
    let params = getPaginationParams(complexesPagination)
    if (isEmpty(filters) || filters.includes('landlord')) {
      handleSelectedLandlordClear()
      params.orgId === undefined
    }
    try {
      await getComplexes(params)
    } catch (error) {
      throw error
    }
  }, [complexesPagination, handleSelectedLandlordClear])

  const handleSearch = React.useCallback(async (value: AutoCompleteResultInterface) => {
    parsedParams.set('s', value.text)
    parsedUrl.search = parsedParams.toString()
    window.history.replaceState({}, '', parsedUrl.toString())
    if (value.darId) {
      try {
        const darLookupResponse = await darLookup(value.darId)
        const url = DEV_DOMAIN + (darLookupResponse.rentalFound ? '/benyt-keyhole' : '/desvaerre') + `?id=${value.darId}`
        process.env.REACT_APP_ENV === 'dev' ? openInNewTab(url) : window.open(url, '_self')
      } catch (error) {
        throw error
      }
    } else {
      const newParams: Pagination = {...complexesPagination, s: value.text}
      setComplexesPagination(newParams)
      try {
        await getComplexes(getPaginationParams(newParams))
        setHasSearched(true)
      } catch (error) {
        throw error
      }
    }
  }, [getComplexes])

  const handleOnLandlordSelect = React.useCallback(async (value: Props['value']) => {
    if (!isEqual(value, selectedLandlord)) {
      if (value) {
        updateSelectedLandlord(value)
      } else {
        handleSelectedLandlordClear()
      }
      try {
        await getComplexes({...getPaginationParams(complexesPagination), orgId: value?.value})
      } catch (error) {
        throw error
      }
    }
  }, [complexesPagination, selectedLandlord, updateSelectedLandlord])

  const handleFetchMore = React.useCallback(async () => {
    try {
      await Promise.all([setAsyncTimeout(800), getComplexes(nextPaginatedParams(complexesPagination), true)])
    } catch (error) {
      throw error
    }
  }, [getComplexes, complexesPagination])

  const mounted = React.useCallback(async (params: GetParams) => {
    try {
      await getComplexes(params)
      setHasSearched(true)
    } catch (error) {
      throw error
    }
  }, [complexesPagination, getComplexes])

  React.useEffect(() => {
    let params = getPaginationParams(complexesPagination)
    if (filters?.organizations) {
      const paramLandlord = parsedParams.get('udlejer')
      if (paramLandlord) {
        const foundLandlord = filters?.organizations?.find(x => x.label === paramLandlord)
        if (foundLandlord) {
          params.orgId = foundLandlord.value
          updateSelectedLandlord({label: foundLandlord.label, value: foundLandlord.value})
        }
      }
      if (!isEmpty(params.s) || !isEmpty(params.orgId)) mounted(params)
    }
  }, [filters])

	return (
		<View
      {...props}
    >
      <BackgroundLayout offset={210}>
        <View
          style={[
            styles.inner,
            {
              paddingHorizontal: dims.width < 992 ? 20 : 32,
              paddingBottom: dims.width > theme.breakpoints.md ? 144 : undefined
            }
          ]}
        >
          <KeyholeDepositAutoCompleteCard
            autoCompleteApiUrl={process.env.REACT_APP_API_URL + '/webflow/autocomplete' || ''}
            dims={dims}
            onClear={handleClear}
            onInputChange={setSearchStr}
            onSearch={handleSearch}
            inputValue={searchStr}
          />
          {(filters?.organizations && (
            <Card style={{
              backgroundColor: theme.colors.green1,
              marginTop: 16,
            }}>
              <View>
                <View style={styles.row}>
                  <Heading style={{color: theme.colors.green, marginBottom: 0}} type="h4">Filter</Heading>
                  {!isEmpty(selectedLandlord) && (
                    <KeyholeTouchableOpacity onPress={() => handleFiltersClear([])}><Paragraph style={{ color: rgba(theme.colors.smoothBlack, .5), fontWeight: '700' }}>Nulstil</Paragraph></KeyholeTouchableOpacity>
                  )}
                </View>
                <Divider
                  noLine
                  style={{ marginVertical: 12 }}
                />
                <View style={{ flex: 1, maxWidth: 400 }}>
                  <Select
                    label="Udlejer"
                    onChange={handleOnLandlordSelect}
                    options={filters?.organizations?.map(filter => ({
                      label: filter.label,
                      value: filter.value,
                    }))}
                    value={selectedLandlord}
                  />
                </View>
              </View>
            </Card>
          ))}
          {!isLoading && !hasSearched && complexes.length < 1 && (
            <Card
              dims={dims}
              mainStyle={{ paddingHorizontal: 0 }}
              style={{
                backgroundColor: theme.colors.red1,
                marginTop: 16,
              }}
            >
              <InfoSection
                desc={<>Gå på opdagelse efter din kommende indskudsfrie lejebolig gennem vores søgbare oversigt for Keyhole Deposit Ejendomme.<Br /><Br />Du kan til hver en tid søge på postnr, by eller specifikke adresser, og finde din kommende indskudsfrie lejebolig.</>}
                dims={dims}
                subContent={
                  <Image
                    source={{ uri: getShapeUrl(3)}}
                    style={{ height: '100%', resizeMode: 'contain' }}
                  />
                }
                title="Søg efter din kommende indskudsfrie lejebolig"
                titleProps={{
                  type: dims.width > theme.breakpoints.md ? 'h2' : 'h4',
                }}
                titleStyle={[
                  styles.infoSectionTitle,
                  {
                    color: theme.colors.red,
                  }
                ]}
              />
            </Card>
          )}
          {(complexes.length > 0 && (
            <>
              <Divider
                noLine
                style={{ marginVertical: 40 }}
              />
              <View style={[styles.listHeader, styles.row, { flexDirection: dims.width > 400 ? 'row' : 'column' }]}>
              <Paragraph><Paragraph style={{ fontWeight: "700" }}>{complexes.length}</Paragraph><Paragraph style={{ opacity: .5 }}>/{complexesPagination.total} ejendomme</Paragraph></Paragraph>
              </View>
              {complexes.map((complex, i) => (
                <View
                  key={complex.id + i}
                  nativeID={i.toString()}
                  style={styles.infoCardWrapper}
                >
                  {i > 0 && <Divider noLine />}
                  <a
                    href={`${DEV_DOMAIN}/kh-ejendomme/listing?id=${complex.id}`}
                    target={process.env.REACT_APP_ENV === 'dev' ? '_blank' : '_self'}
                  >
                    <TouchableOpacity>
                      <InfoCard
                        dims={dims}
                        desc={complex.shortDescription}
                        detailsProps={{
                          data: getComplexDetails(complex)
                        }}
                        subContent={(dims.width > theme.breakpoints.md && complex.lat && complex.long && (
                          <GoogleMapReact
                            bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY }}
                            defaultCenter={{
                              lat: Number(complex.lat),
                              lng: Number(complex.long)
                            }}
                            defaultZoom={14}
                            key={complex.id + i}
                            options={{
                              draggable: false,
                              draggableCursor: 'default',
                              fullscreenControl: false,
                              zoomControl: false,
                              clickableIcons: false,
                              styles: [
                                {
                                  featureType: "poi",
                                  stylers: [
                                    { visibility: "off" }
                                  ]
                                }
                              ]
                            }}
                          >
                            <Marker
                              lat={Number(complex.lat)}
                              lng={Number(complex.long)}
                              style={[themeStyles.marker, styles.marker]}
                            >
                              <BuildingIcon
                                color={theme.colors.green}
                                style={{height: 22, width: 22}}
                              />
                            </Marker>
                          </GoogleMapReact>
                        )) || (
                          <Image
                            source={{ uri: getShapeUrl(i)}}
                            style={{ height: '100%', resizeMode: 'cover' }}
                          />
                        )}
                        subContentStyle={
                          dims.width > 1024 && complex.lat && complex.long
                          ? {width: 460}
                          : undefined
                        }
                        tagline={`${complex.zipcode || ''} ${(complex.city || '')}`}
                        title={complex.name}
                      />
                    </TouchableOpacity>
                  </a>
                </View>
              ))}
              <View style={{ width: '100%' }}>
                <Divider noLine />
                <FetchMoreButton
                  amount={complexes.length}
                  disabled={isLoading || complexes.length >= complexesPagination.total}
                  ghosted={complexes.length === complexesPagination.total}
                  loading={isLoading}
                  onPress={handleFetchMore}
                  total={complexesPagination.total}
                />
              </View>
            </>
          )) || 
          (!isLoading && complexes.length < 1 && hasSearched && (
            <Card
              dims={dims}
              mainStyle={{ paddingHorizontal: 0 }}
              style={{ backgroundColor: theme.colors.green1, marginTop: 40 }}
            >
              <InfoSection
                desc="Du kan til enhver tid ændre dit filter, søge på et andet postnr., by eller specifikke adresser, og finde din kommende indskudsfrie lejebolig."
                dims={dims}
                subContent={
                  <Image
                    source={{ uri: getShapeUrl(2)}}
                    style={{ height: '100%', resizeMode: 'contain' }}
                  />
                }
                title={`Æv.. ${selectedLandlord?.label || 'vi'} har desværre ingen ejendomme i det valgte område`}
                titleProps={{
                  type: dims.width > theme.breakpoints.md ? 'h2' : 'h4',
                }}
                titleStyle={[
                  styles.infoSectionTitle
                ]}
              />
            </Card>
          ))}
          {(isLoading && complexes.length < 1 && (
            <View style={{
              marginTop: 40,
              width: '100%'
            }}>
              <PopulationBar style={{
                height: 27,
                marginBottom: 40,
                width: 150
              }} />
              <PopulationBar
                style={{height: 200, width: '100%'}}
              />
            </View>
          ))}
        </View>
      </BackgroundLayout>
    </View>
	)
}

export interface KeyholeListingsStyles {
  infoCardWrapper: ViewStyle
  infoSectionTitle: TextStyle
  inner: ViewStyle
  listHeader: ViewStyle
  marker: ViewStyle
  row: ViewStyle
  wrapper: ViewStyle
}

const styles = StyleSheet.create<KeyholeListingsStyles>({
  infoCardWrapper: {
    width: '100%',
  },
  infoSectionTitle: {
    marginBottom: 16,
    maxWidth: 550,
  },
  inner: {
    maxWidth: 1280,
    marginHorizontal: 'auto',
    width: '100%',
  },
  listHeader: {
    marginBottom: 40,
  },
  marker: {
    borderWidth: 2,
    height: 80,
    transform: [
      {translateX: -40},
      {translateY: -40}
    ],
    width: 80,
  },
  row: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  wrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    marginHorizontal: 'auto',
    maxWidth: 1200,
    width: '100%',
  },
})

export default KeyholeListings